import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { crearAsientoManual } from "../hooks/useAsientos";
import { CuentaSelectSearch } from "../../catalogo/components/CuentaSelect";
import { useMultimoneda } from "../../../../shared/hooks/useMultimoneda";
import styles from "../../invoices/styles/addInvoice.module.css";

const D      = (n) => Number(n || 0);
const fmt    = (n) => D(n).toLocaleString("es-CR", { minimumFractionDigits: 2 });
const fmtUSD = (n) => D(n).toLocaleString("en-US", { minimumFractionDigits: 2 });

const LINEA_VACIA = () => ({
  _key:        Date.now() + Math.random(),
  cuenta:      null,
  cuentaObj:   null,
  debito:      "",
  credito:     "",
  debito_usd:  "",
  credito_usd: "",
  descripcion: "",
});

export default function AddAsiento() {
  const { t }       = useTranslation();
  const navigate    = useNavigate();
  const multimoneda = useMultimoneda();
  const today       = new Date().toISOString().split("T")[0];

  const [form,   setForm]   = useState({ fecha: today, concepto: "" });
  const [lineas, setLineas] = useState([LINEA_VACIA(), LINEA_VACIA()]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [apiErr, setApiErr] = useState("");

  // ── Totales ──────────────────────────────────────────────────────────────
  const totalD    = lineas.reduce((a, l) => a + D(l.debito),      0);
  const totalC    = lineas.reduce((a, l) => a + D(l.credito),     0);
  const totalDusd = lineas.reduce((a, l) => a + D(l.debito_usd),  0);
  const totalCusd = lineas.reduce((a, l) => a + D(l.credito_usd), 0);

  const diff    = Math.abs(totalD - totalC);
  const diffUsd = Math.abs(totalDusd - totalCusd);
  const hasUsd  = multimoneda && (totalDusd > 0 || totalCusd > 0);

  const crcOk = diff < 0.001 && totalD > 0;
  const usdOk = !hasUsd || diffUsd < 0.001;
  const ok    = crcOk && usdOk;

  // ── Mutadores ────────────────────────────────────────────────────────────
  function setLinea(key, field, value) {
    setLineas((p) => p.map((l) => l._key === key ? { ...l, [field]: value } : l));
  }
  function setCuenta(key, id, obj) {
    setLineas((p) => p.map((l) => l._key === key ? { ...l, cuenta: id, cuentaObj: obj } : l));
  }
  function agregarLinea()   { setLineas((p) => [...p, LINEA_VACIA()]); }
  function eliminarLinea(k) {
    setLineas((prev) => prev.length <= 2 ? prev : prev.filter((l) => l._key !== k));
  }

  // ── Validación ───────────────────────────────────────────────────────────
  function validate() {
    const e = {};
    if (!form.fecha)           e.fecha    = t("common.requerido") || "Requerida.";
    if (!form.concepto.trim()) e.concepto = t("common.requerido") || "Requerido.";
    lineas.forEach((l, i) => {
      if (!l.cuenta)                             e[`l_cuenta_${i}`] = t("asientos.seleccionaCuenta");
      if (!D(l.debito) && !D(l.credito))        e[`l_monto_${i}`]  = t("asientos.ingresaDebitoCredito");
      if (D(l.debito) > 0 && D(l.credito) > 0) e[`l_ambos_${i}`]  = t("asientos.soloDebitoOCredito");
      if (multimoneda && D(l.debito_usd) > 0 && D(l.credito_usd) > 0)
        e[`l_ambos_usd_${i}`] = "USD: solo Debe o Haber por línea.";
    });
    if (!crcOk && !Object.keys(e).some((k) => !k.startsWith("l_")))
      e.balance = `${t("asientos.noBalanceadoDiff")}₡${fmt(diff)}`;
    if (hasUsd && !usdOk && !e.balance)
      e.balance_usd = `Asiento USD no balanceado — Diferencia: $${fmtUSD(diffUsd)}`;
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  // ── Enviar ───────────────────────────────────────────────────────────────
  async function handleSubmit(ev) {
    ev.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setApiErr("");
    try {
      await crearAsientoManual({
        fecha:    form.fecha,
        concepto: form.concepto.trim(),
        lineas:   lineas.map((l) => ({
          cuenta:      l.cuenta,
          debito:      D(l.debito)      || 0,
          credito:     D(l.credito)     || 0,
          debito_usd:  multimoneda ? (D(l.debito_usd)  || 0) : 0,
          credito_usd: multimoneda ? (D(l.credito_usd) || 0) : 0,
          moneda:      multimoneda && (D(l.debito_usd) > 0 || D(l.credito_usd) > 0) ? "USD" : "CRC",
          descripcion: l.descripcion || "",
        })),
      });
      navigate("/finanzas/asientos");
    } catch (e) {
      try {
        const p = JSON.parse(e.message);
        setApiErr(p.lineas?.join(", ") || p.non_field_errors?.join(", ") || JSON.stringify(p));
      } catch { setApiErr(e.message); }
    } finally { setSaving(false); }
  }

  // ── Estilos ──────────────────────────────────────────────────────────────
  const panelStyle = {
    background: "var(--bg-card)",
    border: "1px solid var(--border-card)",
    borderRadius: 6,
    overflow: "hidden",
  };

  const inputStyle = (err) => ({
    width: "100%", boxSizing: "border-box",
    padding: "8px 12px",
    background: "var(--bg-input)",
    border: `1px solid ${err ? "#ef4444" : "var(--border-input)"}`,
    borderRadius: 6,
    color: "var(--text-primary)",
    fontSize: 14,
    outline: "none",
    colorScheme: "light dark",
    transition: "0.3s",
  });

  const labelStyle = {
    display: "block", fontSize: 14, fontWeight: 500,
    color: "var(--text-primary)", marginBottom: 4,
  };

  const colHeaderStyle = {
    fontSize: 12, fontWeight: 600, color: "var(--text-secondary)",
    textTransform: "uppercase", letterSpacing: "0.4px", padding: "10px 12px",
  };

  const errTxt = (msg) => msg
    ? <p style={{ color: "#f87171", fontSize: 12, margin: "3px 0 0" }}>{msg}</p>
    : null;

  // Grid columns — wider when multimoneda to accommodate USD columns
  const GRID = multimoneda
    ? "minmax(160px,2fr) minmax(100px,1fr) 105px 105px 95px 95px 36px"
    : "minmax(180px,2fr) minmax(120px,1fr) 130px 130px 36px";

  return (
    <>
      {/* Breadcrumb */}
      <span className={styles.inactive}>
        <Link to="/"><span className={styles.inactive}>{t("common.inicio")} /</span></Link>
        <Link to="/finanzas/asientos"><span className={styles.inactive}> {t("asientos.titulo")} /</span></Link>
        <span className={styles.active}> {t("asientos.nuevoAsientoManual")}</span>
      </span>

      <h2 style={{ fontSize: 20, fontWeight: 600, color: "var(--text-primary)", margin: "12px 0 20px",
        display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ background: "rgba(249,115,22,0.2)", color: "#f97316", padding: "6px 8px", borderRadius: 8 }}>
          <i className="fa-solid fa-book-open" style={{ color: "#f97316" }} />
        </span>
        {t("asientos.nuevoAsiento")}
        {multimoneda && (
          <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 4,
            background: "rgba(96,165,250,0.12)", color: "#60a5fa",
            border: "1px solid rgba(96,165,250,0.25)", letterSpacing: "0.4px" }}>
            CRC + USD
          </span>
        )}
      </h2>

      <form onSubmit={handleSubmit} style={{ width: "100%", display: "flex", flexDirection: "column", gap: 16 }}>

        {/* ── Encabezado ── */}
        <div style={panelStyle}>
          <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border-card)",
            display: "flex", alignItems: "center", gap: 8 }}>
            <i className="fa-solid fa-circle-info" style={{ color: "#f97316", fontSize: 13 }} />
            <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>
              {t("asientos.encabezado")}
            </span>
          </div>
          <div style={{ padding: 16, display: "grid", gridTemplateColumns: "180px 1fr", gap: 14 }}>
            <div>
              <label style={labelStyle}>{t("common.fecha")} <span style={{ color: "#f87171" }}>*</span></label>
              <input type="date" style={inputStyle(errors.fecha)}
                value={form.fecha}
                onChange={(e) => setForm((p) => ({ ...p, fecha: e.target.value }))} />
              {errTxt(errors.fecha)}
            </div>
            <div>
              <label style={labelStyle}>{t("asientos.concepto")} <span style={{ color: "#f87171" }}>*</span></label>
              <input type="text" style={inputStyle(errors.concepto)}
                placeholder={t("asientos.conceptoPlaceholder")}
                value={form.concepto}
                onChange={(e) => setForm((p) => ({ ...p, concepto: e.target.value }))} />
              {errTxt(errors.concepto)}
            </div>
          </div>
        </div>

        {/* ── Líneas ── */}
        <div style={{ ...panelStyle, overflow: "visible" }}>

          {/* Encabezado columnas */}
          <div style={{ display: "grid", gridTemplateColumns: GRID,
            background: "var(--hover-bg)", borderBottom: "1px solid var(--border-card)" }}>
            <span style={colHeaderStyle}>{t("asientos.cuentaContable")}</span>
            <span style={colHeaderStyle}>{t("common.descripcion")}</span>
            <span style={{ ...colHeaderStyle, textAlign: "right", color: "#4ade80" }}>
              {t("asientos.debeCol")} ₡
            </span>
            <span style={{ ...colHeaderStyle, textAlign: "right", color: "#60a5fa" }}>
              {t("asientos.haberCol")} ₡
            </span>
            {multimoneda && <>
              <span style={{ ...colHeaderStyle, textAlign: "right", color: "#86efac",
                borderLeft: "1px dashed var(--border-card)" }}>
                {t("asientos.debeCol")} $
              </span>
              <span style={{ ...colHeaderStyle, textAlign: "right", color: "#93c5fd" }}>
                {t("asientos.haberCol")} $
              </span>
            </>}
            <span />
          </div>

          {/* Filas */}
          {lineas.map((l, i) => {
            const nat         = l.cuentaObj?.naturaleza;
            const sugiereHaber = nat === "ACREEDORA";
            const sugiereDebe  = nat === "DEUDORA";
            const enContraNat  = (sugiereDebe && Number(l.credito) > 0) ||
                                 (sugiereHaber && Number(l.debito)  > 0);
            const hasLineErr   = errors[`l_monto_${i}`] || errors[`l_ambos_${i}`];
            const hasUsdErr    = errors[`l_ambos_usd_${i}`];

            return (
              <div key={l._key} style={{
                display: "grid", gridTemplateColumns: GRID,
                gap: 8, padding: "8px 12px",
                background: i % 2 === 0 ? "transparent" : "var(--hover-bg)",
                borderBottom: "1px solid var(--border-card)",
                alignItems: "start", overflow: "visible", position: "relative",
                zIndex: lineas.length - i,
              }}>
                {/* Cuenta + badge naturaleza */}
                <div style={{ overflow: "visible" }}>
                  <CuentaSelectSearch
                    value={l.cuenta}
                    onChange={(id, obj) => setCuenta(l._key, id, obj)}
                    placeholder={t("asientos.buscarCuenta")}
                    error={errors[`l_cuenta_${i}`]}
                  />
                  {nat && (
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: 4,
                      marginTop: 4, fontSize: 10, fontWeight: 700,
                      padding: "2px 7px", borderRadius: 4,
                      background: sugiereDebe ? "rgba(248,113,113,0.12)" : "rgba(96,165,250,0.12)",
                      color: sugiereDebe ? "#f87171" : "#60a5fa",
                      border: `1px solid ${sugiereDebe ? "rgba(248,113,113,0.25)" : "rgba(96,165,250,0.25)"}`,
                      letterSpacing: "0.4px",
                    }}>
                      <i className={`fa-solid ${sugiereDebe ? "fa-arrow-left" : "fa-arrow-right"}`}
                         style={{ fontSize: 8 }}/>
                      {sugiereDebe ? t("asientos.naturalDebe") : t("asientos.naturalHaber")}
                      {enContraNat && (
                        <span style={{ color: "#fb923c", marginLeft: 3 }} title="Usando contra-naturaleza">⚡</span>
                      )}
                    </span>
                  )}
                </div>

                {/* Descripción */}
                <input type="text" placeholder={t("common.opcional")}
                  style={inputStyle(false)}
                  value={l.descripcion}
                  onChange={(e) => setLinea(l._key, "descripcion", e.target.value)}
                />

                {/* Debe ₡ */}
                <input type="number" min="0" step="0.01" placeholder="0.00"
                  style={{
                    ...inputStyle(hasLineErr),
                    textAlign: "right", fontFamily: "monospace",
                    borderColor: l.debito
                      ? enContraNat && sugiereHaber ? "rgba(251,146,60,0.5)" : "rgba(248,113,113,0.4)"
                      : undefined,
                  }}
                  value={l.debito}
                  onChange={(e) => {
                    setLinea(l._key, "debito", e.target.value);
                    if (e.target.value) setLinea(l._key, "credito", "");
                  }}
                />

                {/* Haber ₡ */}
                <input type="number" min="0" step="0.01" placeholder="0.00"
                  style={{
                    ...inputStyle(hasLineErr),
                    textAlign: "right", fontFamily: "monospace",
                    borderColor: l.credito
                      ? enContraNat && sugiereDebe ? "rgba(251,146,60,0.5)" : "rgba(96,165,250,0.4)"
                      : undefined,
                  }}
                  value={l.credito}
                  onChange={(e) => {
                    setLinea(l._key, "credito", e.target.value);
                    if (e.target.value) setLinea(l._key, "debito", "");
                  }}
                />

                {/* Debe $ / Haber $ — solo cuando multimoneda */}
                {multimoneda && (
                  <>
                    <input type="number" min="0" step="0.01" placeholder="0.00"
                      style={{
                        ...inputStyle(hasUsdErr),
                        textAlign: "right", fontFamily: "monospace",
                        borderLeft: "1px dashed var(--border-card)",
                        borderColor: l.debito_usd ? "rgba(134,239,172,0.4)" : undefined,
                      }}
                      value={l.debito_usd}
                      onChange={(e) => {
                        setLinea(l._key, "debito_usd", e.target.value);
                        if (e.target.value) setLinea(l._key, "credito_usd", "");
                      }}
                    />
                    <input type="number" min="0" step="0.01" placeholder="0.00"
                      style={{
                        ...inputStyle(hasUsdErr),
                        textAlign: "right", fontFamily: "monospace",
                        borderColor: l.credito_usd ? "rgba(147,197,253,0.4)" : undefined,
                      }}
                      value={l.credito_usd}
                      onChange={(e) => {
                        setLinea(l._key, "credito_usd", e.target.value);
                        if (e.target.value) setLinea(l._key, "debito_usd", "");
                      }}
                    />
                  </>
                )}

                {/* Eliminar */}
                <button type="button" onClick={() => eliminarLinea(l._key)}
                  disabled={lineas.length <= 2}
                  style={{
                    background: "none", border: "none", padding: "8px 4px",
                    color: lineas.length <= 2 ? "var(--border-card)" : "var(--text-secondary)",
                    cursor: lineas.length <= 2 ? "default" : "pointer",
                    fontSize: 14, alignSelf: "center", transition: "color 0.2s",
                  }}
                  onMouseOver={(e) => { if (lineas.length > 2) e.currentTarget.style.color = "#f87171"; }}
                  onMouseOut={(e)  => { e.currentTarget.style.color = lineas.length <= 2 ? "var(--border-card)" : "var(--text-secondary)"; }}
                >
                  <i className="fa-solid fa-xmark" />
                </button>
              </div>
            );
          })}

          {/* Footer totales */}
          <div style={{
            display: "grid", gridTemplateColumns: GRID,
            gap: 8, padding: "10px 12px",
            background: "var(--hover-bg)",
            borderTop: "2px solid var(--border-card)",
            alignItems: "center",
          }}>
            <button type="button" onClick={agregarLinea}
              style={{
                background: "none", border: "1px dashed var(--border-card)",
                borderRadius: 6, padding: "6px 12px",
                color: "var(--text-secondary)", cursor: "pointer", fontSize: 13,
                transition: "all 0.2s",
              }}
              onMouseOver={(e) => { e.currentTarget.style.borderColor = "#f97316"; e.currentTarget.style.color = "#f97316"; }}
              onMouseOut={(e)  => { e.currentTarget.style.borderColor = "var(--border-card)"; e.currentTarget.style.color = "var(--text-secondary)"; }}
            >
              <i className="fa-solid fa-plus" style={{ marginRight: 5 }} />{t("asientos.agregarLinea")}
            </button>

            <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text-secondary)",
              textTransform: "uppercase", letterSpacing: "0.4px", padding: "0 12px" }}>
              {t("asientos.totales").toUpperCase()}
            </span>

            {/* Total Debe ₡ */}
            <span style={{ textAlign: "right", fontFamily: "monospace",
              fontWeight: 700, fontSize: 14, color: "#4ade80", padding: "0 12px" }}>
              ₡{fmt(totalD)}
            </span>

            {/* Total Haber ₡ */}
            <span style={{ textAlign: "right", fontFamily: "monospace",
              fontWeight: 700, fontSize: 14, color: "#60a5fa", padding: "0 12px" }}>
              ₡{fmt(totalC)}
            </span>

            {/* Total Debe $ / Haber $ */}
            {multimoneda && (
              <>
                <span style={{ textAlign: "right", fontFamily: "monospace",
                  fontWeight: 700, fontSize: 14, color: "#86efac", padding: "0 8px",
                  borderLeft: "1px dashed var(--border-card)" }}>
                  ${fmtUSD(totalDusd)}
                </span>
                <span style={{ textAlign: "right", fontFamily: "monospace",
                  fontWeight: 700, fontSize: 14, color: "#93c5fd", padding: "0 8px" }}>
                  ${fmtUSD(totalCusd)}
                </span>
              </>
            )}
            <div />
          </div>
        </div>

        {/* ── Indicador balance CRC ── */}
        <div style={{
          padding: "10px 14px", borderRadius: 6, fontSize: 14,
          display: "flex", alignItems: "center", gap: 10,
          background: crcOk ? "rgba(74,222,128,0.07)"
            : totalD === 0 ? "var(--hover-bg)"
            : "rgba(239,68,68,0.07)",
          border: `1px solid ${crcOk ? "rgba(74,222,128,0.3)"
            : totalD === 0 ? "var(--border-card)"
            : "rgba(239,68,68,0.3)"}`,
        }}>
          <i className={`fa-solid ${crcOk ? "fa-circle-check" : totalD === 0 ? "fa-circle-info" : "fa-triangle-exclamation"}`}
            style={{ color: crcOk ? "#4ade80" : totalD === 0 ? "var(--text-secondary)" : "#f87171" }} />
          <span style={{ color: crcOk ? "#4ade80" : totalD === 0 ? "var(--text-secondary)" : "#f87171" }}>
            {crcOk ? `₡ ${t("asientos.balanceadoListo")}`
              : totalD === 0 ? t("asientos.ingresaMontos")
              : `₡ ${t("asientos.noBalanceadoDiff")}${fmt(diff)}`}
          </span>
        </div>

        {/* ── Indicador balance USD (solo cuando multimoneda y hay montos USD) ── */}
        {multimoneda && hasUsd && (
          <div style={{
            padding: "10px 14px", borderRadius: 6, fontSize: 14,
            display: "flex", alignItems: "center", gap: 10,
            background: usdOk ? "rgba(134,239,172,0.07)" : "rgba(239,68,68,0.07)",
            border: `1px solid ${usdOk ? "rgba(134,239,172,0.3)" : "rgba(239,68,68,0.3)"}`,
          }}>
            <i className={`fa-solid ${usdOk ? "fa-circle-check" : "fa-triangle-exclamation"}`}
              style={{ color: usdOk ? "#86efac" : "#f87171" }} />
            <span style={{ color: usdOk ? "#86efac" : "#f87171" }}>
              {usdOk
                ? `$ ${t("asientos.balanceadoListo")}`
                : `$ ${t("asientos.noBalanceadoDiff")}$${fmtUSD(diffUsd)}`}
            </span>
          </div>
        )}

        {/* ── Errores API ── */}
        {(apiErr || errors.balance || errors.balance_usd) && (
          <div style={{
            padding: "10px 14px", borderRadius: 6, fontSize: 14,
            background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.3)",
            color: "#f87171", display: "flex", flexDirection: "column", gap: 4,
          }}>
            {errors.balance     && <span><i className="fa-solid fa-circle-exclamation" style={{ marginRight: 6 }}/>{errors.balance}</span>}
            {errors.balance_usd && <span><i className="fa-solid fa-circle-exclamation" style={{ marginRight: 6 }}/>{errors.balance_usd}</span>}
            {apiErr             && <span><i className="fa-solid fa-circle-exclamation" style={{ marginRight: 6 }}/>{apiErr}</span>}
          </div>
        )}

        {/* ── Botones ── */}
        <div className={styles.actions} style={{ justifyContent: "flex-start" }}>
          <Link to="/finanzas/asientos" className={`${styles.btn} ${styles["btn-clean-form"]}`}
            style={{ textDecoration: "none" }}>
            {t("common.cancelar")}
          </Link>
          <button type="submit" disabled={saving}
            className={`${styles.btn} ${styles["btn-save"]}`}
            style={{ opacity: saving ? 0.6 : 1 }}>
            {saving
              ? <><i className="fa-solid fa-circle-notch fa-spin" /> {t("catalogo.guardando")}</>
              : <><i className="fa-solid fa-save" /> {t("asientos.guardarAsiento")}</>
            }
          </button>
        </div>
      </form>
    </>
  );
}
